// ═══ LOSS RECOVERY MODAL — auto-pops on -5% day or via Settings (iter 100) ══
// Calls POST /api/omega/loss-recovery with the user's capital + realized
// loss. Displays Kelly-sized per-trade risk, expected trade count, Monte-
// Carlo recovery probability, ruin risk, recommendation, and rationale.
import { useEffect, useState } from "react";
import axios from "axios";
import { hapticTap, hapticWarning } from "../../utils/haptics";
import { Skel } from "./Skel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECO_STYLES = {
  FRESH_START:    { color: "#4ade80", bg: "rgba(74,222,128,0.12)",  border: "rgba(74,222,128,0.32)",  label: "FRESH START" },
  STRICT_KELLY:   { color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  border: "rgba(96,165,250,0.32)",  label: "STRICT KELLY" },
  FRACTIONAL:     { color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.32)",  label: "FRACTIONAL" },
  NOT_ADVISABLE:  { color: "#f87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.32)", label: "NOT ADVISABLE" },
};

export function LossRecoveryModal({ capital, lossAmount, onClose }) {
  const [winRate, setWinRate] = useState(0.55);
  const [winLossRatio, setWinLossRatio] = useState(1.8);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // iter 131 — activation flow
  const [activeStatus, setActiveStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activateError, setActivateError] = useState("");

  const refreshActive = async () => {
    try {
      const r = await axios.get(`${API}/omega/loss-recovery/active`);
      setActiveStatus(r.data);
    } catch { /* silent — modal still useful for the math */ }
  };

  const activate = async () => {
    if (!plan?.canActivate) return;
    setBusy(true); setActivateError("");
    try {
      try { hapticTap && hapticTap(); } catch {}
      await axios.post(`${API}/omega/loss-recovery/activate`, { plan });
      await refreshActive();
    } catch (e) {
      setActivateError(e?.response?.data?.detail || e?.message || "Failed to engage");
    }
    setBusy(false);
  };

  const deactivate = async () => {
    setBusy(true); setActivateError("");
    try {
      try { hapticTap && hapticTap(); } catch {}
      await axios.post(`${API}/omega/loss-recovery/deactivate`);
      await refreshActive();
    } catch (e) {
      setActivateError(e?.response?.data?.detail || e?.message || "Failed to stop");
    }
    setBusy(false);
  };

  const compute = async (wr = winRate, wl = winLossRatio) => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/omega/loss-recovery`, {
        capital, lossAmount, winRate: wr, winLossRatio: wl, maxRiskPct: 0.10,
      });
      if (r.data?.ok) setPlan(r.data);
      else setError(r.data?.error || "Unable to compute plan");
    } catch (e) {
      setError(e?.message || "Unable to compute plan");
    }
    setLoading(false);
  };

  useEffect(() => {
    try { hapticWarning && hapticWarning(); } catch {}
    compute();
    refreshActive();
    // eslint-disable-next-line
  }, []);

  const reco = plan ? (RECO_STYLES[plan.recommendation] || RECO_STYLES.STRICT_KELLY) : RECO_STYLES.STRICT_KELLY;

  return (
    <div
      data-testid="loss-recovery-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 20040,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          background: "var(--bg, #060a12)", border: `1px solid ${reco.border}`,
          padding: "24px 20px 30px", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: reco.color, animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: reco.color, letterSpacing: 2 }}>OMEGA ZI · LOSS RECOVERY PLAN</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--muted)", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text)", letterSpacing: -0.4, marginBottom: 3 }}>
          Recover ${lossAmount?.toLocaleString?.(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 16 }}>
          On ${capital?.toLocaleString?.(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} capital · Monte-Carlo simulated plan
        </div>

        {/* Assumption sliders */}
        <div style={{ padding: 14, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 1.5, marginBottom: 10 }}>YOUR ASSUMPTIONS</div>

          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text)", marginBottom: 4 }}>
              <span>Win rate</span>
              <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{(winRate * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="lr-winrate-slider"
              type="range" min="0.30" max="0.80" step="0.01" value={winRate}
              onChange={(e) => setWinRate(parseFloat(e.target.value))}
              onMouseUp={(e) => compute(parseFloat(e.target.value), winLossRatio)}
              onTouchEnd={(e) => compute(parseFloat(e.target.value), winLossRatio)}
              style={{ width: "100%", accentColor: reco.color }}
            />
          </label>

          <label style={{ display: "block" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text)", marginBottom: 4 }}>
              <span>Avg W : L ratio</span>
              <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{winLossRatio.toFixed(1)}</span>
            </div>
            <input
              data-testid="lr-wl-slider"
              type="range" min="0.8" max="4.0" step="0.1" value={winLossRatio}
              onChange={(e) => setWinLossRatio(parseFloat(e.target.value))}
              onMouseUp={(e) => compute(winRate, parseFloat(e.target.value))}
              onTouchEnd={(e) => compute(winRate, parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: reco.color }}
            />
          </label>
        </div>

        {loading && <><Skel w="100%" h={100} mb={8} radius={12} /><Skel w="100%" h={60} mb={0} radius={12} /></>}
        {error && <div style={{ padding: 12, borderRadius: 10, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", fontSize: 12, color: "#f87171" }}>{error}</div>}

        {!loading && plan && (
          <>
            {/* Recommendation card with strategy bullets + activate button */}
            <div data-testid="lr-reco" style={{
              padding: 16, borderRadius: 14, marginBottom: 12,
              background: reco.bg, border: `1px solid ${reco.border}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: reco.color, letterSpacing: 1, marginBottom: 6 }}>{reco.label}</div>
              <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.55, marginBottom: plan.strategy?.length ? 12 : 0 }}>{plan.rationale}</div>
              {Array.isArray(plan.strategy) && plan.strategy.length > 0 && (
                <div data-testid="lr-strategy-list" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: reco.color, letterSpacing: 1.3, opacity: 0.8 }}>
                    {plan.canActivate ? "OMEGA'S RECOVERY STRATEGY" : "WHAT YOU SHOULD DO NEXT"}
                  </div>
                  {plan.strategy.map((s, i) => (
                    <div key={i} data-testid={`lr-strategy-${i}`} style={{
                      fontSize: 12, color: "var(--text)", lineHeight: 1.5,
                      paddingLeft: 18, position: "relative",
                    }}>
                      <span style={{ position: "absolute", left: 0, top: 0, color: reco.color, fontWeight: 800 }}>›</span>
                      {s}
                    </div>
                  ))}
                </div>
              )}
              {plan.canActivate && (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  {activeStatus?.active ? (
                    <>
                      <div data-testid="lr-active-banner" style={{ fontSize: 11, color: reco.color, fontWeight: 700, textAlign: "center", padding: "8px 10px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: `1px solid ${reco.border}` }}>
                        ● Omega auto-recovery ACTIVE
                        {activeStatus.plan?.progress_pct != null && (
                          <span style={{ marginLeft: 8, opacity: 0.85 }}>
                            {activeStatus.plan.progress_pct}% recovered (${activeStatus.plan.recovered ?? 0} / ${plan.loss?.toFixed?.(2)})
                          </span>
                        )}
                      </div>
                      <button
                        data-testid="lr-deactivate-btn"
                        onClick={deactivate}
                        disabled={busy}
                        style={{
                          width: "100%", padding: "11px 14px", borderRadius: 12,
                          background: "rgba(248,113,113,0.12)",
                          border: "1px solid rgba(248,113,113,0.35)",
                          color: "#f87171", fontWeight: 800, fontSize: 13,
                          cursor: busy ? "wait" : "pointer", letterSpacing: 0.4,
                        }}
                      >
                        {busy ? "Working…" : "Stop Auto-Recovery"}
                      </button>
                    </>
                  ) : (
                    <button
                      data-testid="lr-activate-btn"
                      onClick={activate}
                      disabled={busy}
                      style={{
                        width: "100%", padding: "13px 14px", borderRadius: 12,
                        background: `linear-gradient(135deg, ${reco.color}, ${reco.color}cc)`,
                        border: "none", color: "#0a0a14", fontWeight: 900, fontSize: 13,
                        cursor: busy ? "wait" : "pointer", letterSpacing: 0.5,
                        boxShadow: `0 6px 22px ${reco.color}44`,
                        textTransform: "uppercase",
                      }}
                    >
                      {busy ? "Engaging…" : "🚀 Engage Omega Auto-Recovery"}
                    </button>
                  )}
                  {activateError && <div style={{ fontSize: 11, color: "#f87171", textAlign: "center" }}>{activateError}</div>}
                </div>
              )}
            </div>

            {/* Key numbers grid */}
            {plan.perTradeRisk > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                <Stat label="Per-trade risk" value={`$${plan.perTradeRisk.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                <Stat label="Expected trades" value={plan.expectedTrades || "—"} />
                <Stat label="Recovery prob" value={`${Math.round((plan.breakEvenProb || 0) * 100)}%`} positive={plan.breakEvenProb >= 0.6} />
                <Stat label="Ruin risk" value={`${Math.round((plan.ruinRisk || 0) * 100)}%`} danger={plan.ruinRisk >= 0.15} />
                <Stat label="Expectancy / trade" value={`$${(plan.expectancyPerTrade || 0).toFixed(2)}`} positive={plan.expectancyPerTrade >= 0} />
                <Stat label="Kelly f*" value={`${((plan.kellyFractionFull || 0) * 100).toFixed(1)}%`} />
              </div>
            )}

            <div style={{ fontSize: 10, color: "var(--muted)", textAlign: "center", marginTop: 10, fontStyle: "italic", opacity: 0.8 }}>
              1,000-path Monte-Carlo simulation · Quarter-Kelly sizing by default · Capital-at-half = ruin threshold
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, positive, danger }) {
  const color = danger ? "#f87171" : (positive ? "#4ade80" : "var(--text)");
  return (
    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.3, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
